// NotificationBell shared strings — EN + TR.
//
// Wave-1 Day 2 wiring. Bell chrome ("Notifications" / "recent" / "No new events")
// + event titles (~20 distinct types) + locale-aware PEXIFY scrub token for the
// seller-leak defense-in-depth invariant (feedback_seller_surface_no_supplier_leak).
//
// pexifyScrubToken important: TR variant intentionally uses lower-case "üretim"
// (NOT "Üretici") so the scrub replacement does not collide with the
// SELLER_LEAK_TOKENS regex (which catches "Üretici"). The replacement token
// must be safe under further re-application.

import type { LocalizedDict } from '../index'

type NotificationBellStrings = {
  bellAriaLabel: string
  closeAriaLabel: string
  headerTitle: string
  recentSuffix: string
  emptyState: string
  tabMine: string
  tabSystem: string
  markAllRead: string
  mineEmpty: string
  pexifyScrubToken: string
  eventTitles: {
    poAcknowledged: string
    poInTransit: string
    poReceived: string
    poDisputed: string
    poClosed: string
    poCancelled: string
    poInboxArrival: string
    receivingQcQuarantined: string
    reorderThresholdBreached: string
    inventoryRealOutOfStock: string
    allocationReleasedBySellerSelf: string
    allocationReleasedBySellerAdmin: string
    ordersReturnRequested: string
    ordersHasIssues: string
    vendorReplacementAffectingSeller: string
    etsyPushUncertainReminder: string
    vendorReplacementAffectingAdmin: string
    workOrderAckOverdue: string
    workOrderDeadlineApproaching: string
    qcDispositionPending: string
    etsyTrackingStaleAfterReplacement: string
    ebayTrackingStaleAfterVoid: string
    shopifyTrackingStaleAfterVoid: string
    channelPushSnapshotUnreconciled: string
  }
}

export const notificationBellI18n: LocalizedDict<NotificationBellStrings> = {
  en: {
    bellAriaLabel: 'Notifications',
    closeAriaLabel: 'Close notifications',
    headerTitle: 'Notifications',
    recentSuffix: 'recent',
    emptyState: 'No new events.',
    tabMine: 'For you',
    tabSystem: 'System',
    markAllRead: 'Mark all read',
    mineEmpty: 'Nothing for you yet.',
    pexifyScrubToken: 'PEXIFY production',
    eventTitles: {
      poAcknowledged: 'PO acknowledged',
      poInTransit: 'PO in transit',
      poReceived: 'PO received',
      poDisputed: 'PO disputed',
      poClosed: 'PO closed',
      poCancelled: 'PO cancelled',
      poInboxArrival: 'New PO in inbox',
      receivingQcQuarantined: 'QC quarantined line',
      reorderThresholdBreached: 'Reorder threshold breached',
      inventoryRealOutOfStock: 'Out of stock',
      allocationReleasedBySellerSelf: 'Allocation released',
      allocationReleasedBySellerAdmin: 'Seller released allocation',
      ordersReturnRequested: 'Return requested',
      ordersHasIssues: 'Order has issues',
      vendorReplacementAffectingSeller: 'Listing may be affected by a supplier change',
      etsyPushUncertainReminder: 'Etsy send still unresolved — check your Etsy drafts',
      vendorReplacementAffectingAdmin: 'Vendor replacement affecting listed product',
      workOrderAckOverdue: 'Work order acknowledgement overdue',
      workOrderDeadlineApproaching: 'Work order deadline approaching',
      qcDispositionPending: 'QC disposition pending',
      etsyTrackingStaleAfterReplacement: 'Etsy tracking needs a manual fix',
      ebayTrackingStaleAfterVoid: 'eBay received tracking of a voided label',
      shopifyTrackingStaleAfterVoid: 'Shopify received tracking of a voided label',
      channelPushSnapshotUnreconciled: 'Voided label: marketplace tracking needs a check',
    },
  },
  tr: {
    bellAriaLabel: 'Bildirimler',
    closeAriaLabel: 'Bildirimleri kapat',
    headerTitle: 'Bildirimler',
    recentSuffix: 'son',
    emptyState: 'Yeni etkinlik yok.',
    tabMine: 'Sana',
    tabSystem: 'Sistem',
    markAllRead: 'Tümünü okundu yap',
    mineEmpty: 'Sana gelen bir şey yok.',
    pexifyScrubToken: 'PEXIFY üretim',
    eventTitles: {
      poAcknowledged: 'PO onaylandı',
      poInTransit: 'PO yolda',
      poReceived: 'PO teslim alındı',
      poDisputed: 'PO itiraz edildi',
      poClosed: 'PO kapatıldı',
      poCancelled: 'PO iptal edildi',
      poInboxArrival: 'Gelen kutusunda yeni PO',
      receivingQcQuarantined: 'QC karantinaya alındı',
      reorderThresholdBreached: 'Yeniden sipariş eşiği aşıldı',
      inventoryRealOutOfStock: 'Stok tükendi',
      allocationReleasedBySellerSelf: 'Tahsis serbest bırakıldı',
      allocationReleasedBySellerAdmin: 'Satıcı tahsisi serbest bıraktı',
      ordersReturnRequested: 'İade talep edildi',
      ordersHasIssues: 'Siparişte sorun var',
      vendorReplacementAffectingSeller: 'Listelenmiş ürün bir tedarik değişiminden etkilenebilir',
      etsyPushUncertainReminder: 'Etsy gönderimi hâlâ belirsiz — Etsy taslaklarınıza bakın',
      vendorReplacementAffectingAdmin: 'Listelenmiş ürünü etkileyen tedarikçi değişimi',
      workOrderAckOverdue: 'İş emri onayı gecikti',
      workOrderDeadlineApproaching: 'İş emri teslim tarihi yaklaşıyor',
      qcDispositionPending: 'QC kararı bekliyor',
      etsyTrackingStaleAfterReplacement: 'Etsy kargo takibi elle düzeltilmeli',
      ebayTrackingStaleAfterVoid: "eBay'e iptal edilmiş etiketin takibi gitti",
      shopifyTrackingStaleAfterVoid: "Shopify'a iptal edilmiş etiketin takibi gitti",
      channelPushSnapshotUnreconciled: 'İptal edilen etiket: pazar yeri takibi kontrol edilmeli',
    },
  },
}
